import { Injectable } from '@angular/core';
import { Platform } from '@ionic/angular/standalone';
import { App } from '@capacitor/app';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { catchError, map } from 'rxjs/operators';
import { of } from 'rxjs';
import { LoggerService } from './logger.service';

export interface AppInfo {
  version: string;
  buildNumber: string;
  platform: string;
  isNative: boolean;
}

export interface VersionInfo {
  android: {
    minVersion: string;
    latestVersion: string;
  };
  ios: {
    minVersion: string;
    latestVersion: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class AppInfoService {
  private appInfo: AppInfo | null = null;

  constructor(
    private platform: Platform,
    private http: HttpClient,
    private logger: LoggerService
  ) {}

  /**
   * Get the current app information
   */
  async getAppInfo(): Promise<AppInfo> {
    if (this.appInfo) {
      return this.appInfo;
    }

    try {
      if (this.platform.is('capacitor')) {
        // Running on native device
        const appInfo = await App.getInfo();
        
        this.appInfo = {
          version: appInfo.version,
          buildNumber: appInfo.build,
          platform: this.platform.platforms().includes('android') ? 'android' : 'ios',
          isNative: true
        };
      } else {
        // Running in web browser - fetch version from S3 bucket
        const versionInfo = await this.getWebVersion().toPromise();
        const webVersion = versionInfo?.android?.latestVersion || '1.0.22';
        const buildNumber = webVersion.split('.').pop() || '22';
        
        this.appInfo = {
          version: webVersion,
          buildNumber: buildNumber,
          platform: 'web',
          isNative: false
        };
      }
    } catch (error) {
      this.logger.error('Failed to get app info:', error);
      // Fallback values
      this.appInfo = {
        version: '1.0.22',
        buildNumber: '22',
        platform: 'unknown',
        isNative: false
      };
    }

    return this.appInfo;
  }

  /**
   * Get version information from the S3 bucket's version.json file
   */
  private getWebVersion() {
    // Determine which version endpoint to use based on environment
    let versionUrl: string;
    
    switch (environment.environmentName) {
      case 'staging':
        versionUrl = 'https://app.stage.piledco.com/version.json';
        break;
      case 'production':
        versionUrl = 'https://app.us.piledco.com/version.json';
        break;
      case 'development':
      case 'local':
      default:
        versionUrl = 'https://app.dev.piledco.com/version.json';
        break;
    }

    return this.http.get<VersionInfo>(versionUrl).pipe(
      map(response => response),
      catchError(error => {
        this.logger.error('Failed to fetch web version:', error);
        return of(null);
      })
    );
  }

  /**
   * Get just the version string
   */
  async getVersion(): Promise<string> {
    const appInfo = await this.getAppInfo();
    return appInfo.version;
  }

  /**
   * Get just the build number
   */
  async getBuildNumber(): Promise<string> {
    const appInfo = await this.getAppInfo();
    return appInfo.buildNumber;
  }

  /**
   * Check if running on native device
   */
  isNativeDevice(): boolean {
    return this.platform.is('capacitor');
  }

  /**
   * Get platform information
   */
  async getPlatform(): Promise<string> {
    const appInfo = await this.getAppInfo();
    return appInfo.platform;
  }
} 